import React, { FC, useMemo } from 'react'
import { useFormik } from 'formik'
import { ILenderParams } from 'typescript/interfaces/deals'
import Grid from '@material-ui/core/Grid'
import Checkbox from 'UI/Checkbox'
import Typography from '@material-ui/core/Typography'
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel'
import Box from '@material-ui/core/Box'
import Button from 'UI/Button/ButtonWithPreloader'
import { assignLenderSchema } from 'components/AssignLenderForm/schema'
import SyndicatorAutoCompleteWithBalance from 'components/SyndicatorAutoCompleteWithBalance'
import { mathRoundingAmount, mathRoundingPercentage } from 'utils/rounding'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import { DECIMAL_PLACES, DECIMAL_PLACES_PERCENTAGE } from 'constants/calcConstants'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Paper from '@material-ui/core/Paper'

const useAlertStyles = makeStyles(theme => ({
  alert: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    border: '1px solid',
  },
  warning: {
    backgroundColor: theme.palette.warning.light || '#FFF3CD',
    borderColor: theme.palette.warning.main || '#DCAC00',
    color: '#000000',
  },
  error: {
    backgroundColor: theme.palette.error.light || '#F8D7DA',
    borderColor: theme.palette.error.main || '#DC3545',
    color: theme.palette.error.dark || '#721C24',
  },
  info: {
    backgroundColor: theme.palette.info.light || '#e3f2fd',
    borderColor: theme.palette.info.main || '#2196f3',
    color: theme.palette.info.dark || '#1976d2',
  },
  alertTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}))

interface IAssignLenderForm {
  submit: (values: ILenderParams) => void
  initValue?: ILenderParams
  dealId: string
  cancel: () => void
  loading?: boolean
  mode?: string
  advanceAmount: number
  currentTotalPercentage?: number
  currentLenderPercentage?: number
  fullAdvanceAmount?: number
  onFormChange?: () => void
}

interface IAssignLenderFormInitValues extends ILenderParams {
  syndicatorName?: string
  syndicatorBalance?: number
}

const AssignLenderForm: FC<IAssignLenderForm> = ({
  submit,
  dealId,
  cancel,
  initValue,
  loading,
  mode,
  advanceAmount,
  currentTotalPercentage = 0,
  currentLenderPercentage = 0,
  fullAdvanceAmount,
  onFormChange,
}) => {
  const { values, setFieldValue, errors, touched, handleSubmit, setValues } = useFormik<
    IAssignLenderFormInitValues
  >({
    initialValues: initValue || {
      syndicatorId: '',
      syndicatorName: '',
      lenderId: 0,
      dealId: dealId,
      lendingPercentage: '',
      isLead: false,
      lendingAmount: '',
      syndicatorBalance: 0,
    },
    validationSchema: assignLenderSchema,
    onSubmit: submit,
  })

  // Calculate validation warnings and errors
  const validationWarnings = useMemo(() => {
    const warnings = []
    const percentage = Number(values.lendingPercentage) || 0

    // Check if this would exceed 100% total
    // When editing, subtract the current lender's percentage from the total
    const effectiveTotalPercentage =
      mode === 'edit' ? currentTotalPercentage - currentLenderPercentage : currentTotalPercentage

    if (effectiveTotalPercentage + percentage > 100.01) {
      warnings.push(`Adding ${percentage}% would exceed 100% total participation`)
    }

    // Check main lender percentage requirement
    if (values.isLead && percentage < 25) {
      warnings.push('Main lenders must have at least 25% participation')
    }

    return warnings
  }, [
    values.lendingPercentage,
    values.lendingAmount,
    values.isLead,
    currentTotalPercentage,
    currentLenderPercentage,
    mode,
    advanceAmount,
  ])

  const insufficientFundsError = useMemo(() => {
    const initLendingAmount = Number(initValue?.lendingAmount)
    const newLendingAmount = (fullAdvanceAmount || advanceAmount) * (Number(values.lendingPercentage) / 100)
    const syndicatorBalance = values.syndicatorBalance || 0
    const lackOfAmmount = newLendingAmount > initLendingAmount ? newLendingAmount - initLendingAmount : 0

    if (lackOfAmmount > 0 && lackOfAmmount > syndicatorBalance) {
      return `Syndicator has insufficient funds, $${lackOfAmmount.toLocaleString()} is required`
    }
    return null
  }, [values.lendingPercentage, values.syndicatorBalance, fullAdvanceAmount, advanceAmount])

  const hasErrors = Object.keys(errors).length > 0
  const hasWarnings = validationWarnings.length > 0
  const hasInsufficientFunds = insufficientFundsError !== null
  const isFormValid =
    !hasErrors &&
    !hasWarnings &&
    !hasInsufficientFunds &&
    values.syndicatorId &&
    values.lendingPercentage &&
    values.lendingAmount
  const alertClasses = useAlertStyles()

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {mode === 'edit' ? 'Edit Lender' : 'Add New Lender'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <SyndicatorAutoCompleteWithBalance
            disabled={mode === 'edit'}
            error={Boolean(touched.syndicatorId && errors.syndicatorId)}
            helperText={touched.syndicatorId && errors.syndicatorId}
            syndicatorId={values.syndicatorId}
            onChange={(e, value) => {
              setFieldValue('syndicatorId', value?.id || '')
              setFieldValue('syndicatorBalance', value?.availableBalance || 0)
              onFormChange?.()
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <DecimalTextField
            fullWidth
            label="Participation Percentage"
            name="lendingPercentage"
            decimalScale={DECIMAL_PLACES_PERCENTAGE}
            value={values.lendingPercentage}
            onChange={e => {
              setValues({
                ...values,
                lendingPercentage: e.target.value,
                lendingAmount:
                  e.target.value === '' || Number(e.target.value) < 0
                    ? ''
                    : mathRoundingAmount((+e.target.value / 100) * advanceAmount),
              })
              onFormChange?.()
            }}
            onBlur={() => {
              if (values.lendingPercentage !== '') {
                setValues({
                  ...values,
                  lendingPercentage: mathRoundingPercentage(+values.lendingPercentage),
                  lendingAmount:
                    values.lendingPercentage === '' || Number(values.lendingPercentage) < 0
                      ? ''
                      : mathRoundingAmount((+values.lendingPercentage / 100) * advanceAmount),
                })
              }
            }}
            variant="outlined"
            error={Boolean(touched.lendingPercentage && errors.lendingPercentage)}
            helperText={
              touched.lendingPercentage && errors.lendingPercentage ? errors.lendingPercentage : ''
            }
          />
        </Grid>

        <Grid item xs={12}>
          <DecimalTextField
            fullWidth
            label="Participation Amount"
            name="lendingAmount"
            decimalScale={DECIMAL_PLACES}
            value={values.lendingAmount}
            onChange={e => {
              setValues({
                ...values,
                lendingAmount: e.target.value,
                lendingPercentage:
                  e.target.value === '' ||
                  Number(e.target.value) < 0 ||
                  !advanceAmount ||
                  advanceAmount === 0
                    ? ''
                    : mathRoundingPercentage((+e.target.value * 100) / advanceAmount),
              })
              onFormChange?.()
            }}
            onBlur={() => {
              if (values.lendingAmount !== '') {
                setValues({
                  ...values,
                  lendingAmount: mathRoundingAmount(+values.lendingAmount),
                  lendingPercentage:
                    values.lendingAmount === '' ||
                    Number(values.lendingAmount) < 0 ||
                    !advanceAmount ||
                    advanceAmount === 0
                      ? ''
                      : mathRoundingPercentage((+values.lendingAmount * 100) / advanceAmount),
                })
              }
            }}
            variant="outlined"
            error={Boolean(touched.lendingAmount && errors.lendingAmount)}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="isLead"
                checked={values.isLead}
                color="primary"
                onChange={e => {
                  setFieldValue('isLead', e.target.checked)
                  onFormChange?.()
                }}
              />
            }
            label={
              <Box>
                <Typography color="textSecondary">Main Lender</Typography>
                <Typography variant="caption" color="textSecondary">
                  Main lenders must have at least 25% participation
                </Typography>
              </Box>
            }
          />
        </Grid>

        {insufficientFundsError && (
          <Grid item xs={12}>
            <Paper className={`${alertClasses.alert} ${alertClasses.warning}`} elevation={0}>
              <Typography variant="h6" className={alertClasses.alertTitle}>
                Insufficient Funds
              </Typography>
              <Typography variant="body2">{insufficientFundsError}</Typography>
            </Paper>
          </Grid>
        )}

        {validationWarnings.length > 0 && (
          <Grid item xs={12}>
            <Paper className={`${alertClasses.alert} ${alertClasses.warning}`} elevation={0}>
              <Typography variant="h6" className={alertClasses.alertTitle}>
                Validation Warnings
              </Typography>
              <Box>
                {validationWarnings.map(warning => (
                  <Typography
                    key={`warning-${warning.replace(/[^a-zA-Z0-9]/g, '').substring(0, 15)}`}
                    variant="body2"
                    gutterBottom
                  >
                    • {warning}
                  </Typography>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button variant="contained" color="secondary" onClick={cancel} disabled={loading}>
              Cancel
            </Button>
            <Box ml={2}>
              <Button
                loading={loading}
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isFormValid || loading}
              >
                {mode === 'edit' ? 'Update' : 'Add'} Lender
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </form>
  )
}

export default AssignLenderForm
